import { PageContainer } from '../layout/PageContainer';

export function PrivacyPage() {
  return (
    <PageContainer>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-white mb-2">Privacy Policy</h1>
        <p className="text-slate-400 mb-8">Last updated: February 2026</p>

        <div className="prose prose-invert prose-slate max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              1. Introduction
            </h2>
            <p className="text-slate-300 leading-relaxed">
              Welcome to After5PM (&quot;we,&quot; &quot;our,&quot; or
              &quot;us&quot;). We respect your privacy and are committed to
              protecting your personal data. This privacy policy explains how we
              collect, use, and safeguard your information when you use our
              gaming platform at after5pm.fun.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              2. Information We Collect
            </h2>

            <h3 className="text-xl font-medium text-white mt-6 mb-3">
              Account Information
            </h3>
            <ul className="list-disc pl-6 text-slate-300 space-y-2">
              <li>Email address (for account creation and communication)</li>
              <li>Username (displayed on leaderboards)</li>
              <li>Password (stored securely using AWS Cognito)</li>
            </ul>

            <h3 className="text-xl font-medium text-white mt-6 mb-3">
              Usage Data
            </h3>
            <ul className="list-disc pl-6 text-slate-300 space-y-2">
              <li>Games played and high scores</li>
              <li>Session duration and frequency</li>
              <li>Device type and browser information</li>
              <li>IP address (for security and analytics)</li>
            </ul>

            <h3 className="text-xl font-medium text-white mt-6 mb-3">
              Payment Information
            </h3>
            <p className="text-slate-300">
              If you subscribe to our premium service, payment processing is
              handled by Stripe. We do not store your credit card details on our
              servers. Stripe&apos;s privacy policy applies to payment data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              3. How We Use Your Information
            </h2>
            <ul className="list-disc pl-6 text-slate-300 space-y-2">
              <li>Provide and maintain our gaming service</li>
              <li>Display your username on public leaderboards</li>
              <li>Process your subscription payments</li>
              <li>Send important account notifications</li>
              <li>Improve our games and user experience</li>
              <li>Detect and prevent fraud or abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              4. Data Sharing
            </h2>
            <p className="text-slate-300 mb-4">
              We do not sell your personal information. We may share data with:
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2">
              <li>
                <strong>Service Providers:</strong> AWS (hosting), Stripe
                (payments), analytics providers
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law or to
                protect our rights
              </li>
              <li>
                <strong>Business Transfers:</strong> In case of merger,
                acquisition, or sale
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              5. Data Retention
            </h2>
            <p className="text-slate-300">
              We retain your account data for as long as your account is active.
              High scores remain on leaderboards indefinitely unless you request
              deletion. You can request account deletion at any time by
              contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              6. Your Rights
            </h2>
            <p className="text-slate-300 mb-4">
              Depending on your location, you may have the right to:
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Delete your data (&quot;right to be forgotten&quot;)</li>
              <li>Export your data in a portable format</li>
              <li>Opt out of marketing communications</li>
              <li>Withdraw consent for optional data processing</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              7. Cookies
            </h2>
            <p className="text-slate-300">
              We use cookies and similar technologies for authentication,
              preferences, and analytics. See our{' '}
              <a
                href="/cookies"
                className="text-purple-400 hover:text-purple-300"
              >
                Cookie Policy
              </a>{' '}
              for details and controls.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              8. Security
            </h2>
            <p className="text-slate-300">
              We implement industry-standard security measures including
              encryption in transit (HTTPS) and at rest (AES-256), secure
              authentication via AWS Cognito, and regular security audits.
              However, no system is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              9. Children&apos;s Privacy
            </h2>
            <p className="text-slate-300">
              Our service is not intended for children under 13. We do not
              knowingly collect data from children. If you believe a child has
              provided us data, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              10. International Transfers
            </h2>
            <p className="text-slate-300">
              Your data may be processed in the United States where our servers
              are located. By using our service, you consent to this transfer.
              We ensure appropriate safeguards are in place for international
              data transfers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              11. Changes to This Policy
            </h2>
            <p className="text-slate-300">
              We may update this policy periodically. We&apos;ll notify you of
              significant changes via email or a notice on our site. Continued
              use after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              12. Contact Us
            </h2>
            <p className="text-slate-300">
              For privacy questions or to exercise your rights, contact us at:{' '}
              <a
                href="mailto:privacy@after5pm.fun"
                className="text-purple-400 hover:text-purple-300"
              >
                privacy@after5pm.fun
              </a>
            </p>
          </section>
        </div>
      </div>
    </PageContainer>
  );
}
